import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api/api";

const AuthContext = createContext(null);
const USER_KEY = "etf_auth_user";
const TOKEN_KEY = "etf_token";

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.role) return null;
    return parsed;
  } catch (err) {
    return null;
  }
};

const mapRole = (typecode) => {
  if (typecode === "A") return "admin";
  return "member";
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => readStoredUser());
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) {
      localStorage.removeItem(USER_KEY);
      return;
    }
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser((prev) => (prev?.role === "free" ? prev : null));
      setStatus("ready");
      return;
    }

    let active = true;
    setStatus("loading");
    apiFetch("/api/v1/auth/etf/me")
      .then((data) => {
        if (!active) return;
        const role = mapRole(data?.user?.typecode);
        setUser({
          role,
          name: data?.user?.fullname || "Investor",
          email: data?.user?.email || "",
          phone: data?.user?.phone || "",
          typecode: data?.user?.typecode || "",
        });
        setStatus("ready");
      })
      .catch(() => {
        if (!active) return;
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
        setStatus("ready");
      });

    return () => {
      active = false;
    };
  }, []);

  const api = useMemo(() => {
    const isMember = user?.role === "member";
    const isAdmin = user?.role === "admin";
    const isFree = user?.role === "free";

    return {
      user,
      status,
      error,
      isAuthenticated: isMember || isAdmin,
      isMember,
      isAdmin,
      isFree,
      login: async ({ login, password }) => {
        setStatus("loading");
        setError("");
        if (!login || !password) {
          setStatus("error");
          setError("Login and password are required");
          return { ok: false };
        }
        try {
          const data = await apiFetch("/api/v1/auth/etf/login", {
            method: "POST",
            body: JSON.stringify({ login, password }),
          });
          if (data?.token) localStorage.setItem(TOKEN_KEY, data.token);
          const role = mapRole(data?.user?.typecode);
          setUser({
            role,
            name: data?.user?.fullname || "Investor",
            email: data?.user?.email || "",
            phone: data?.user?.phone || "",
            typecode: data?.user?.typecode || "",
          });
          setStatus("ready");
          return { ok: true, role };
        } catch (err) {
          setStatus("error");
          setError(err.message || "Login failed");
          return { ok: false };
        }
      },
      register: async (payload) => {
        setStatus("loading");
        setError("");
        if (!payload?.username) {
          setStatus("error");
          setError("Email or phone is required");
          return { ok: false };
        }
        if (!payload?.password || payload.password.length < 6) {
          setStatus("error");
          setError("Password must be at least 6 characters");
          return { ok: false };
        }
        try {
          const data = await apiFetch("/api/v1/auth/admin/etf/register", {
            method: "POST",
            body: JSON.stringify({
              fullname: payload?.name || "Investor",
              username: payload?.username,
              email: payload?.email || null,
              phone: payload?.phone || null,
              password: payload?.password,
              typecode: "U",
            }),
          });
          if (data?.token) localStorage.setItem(TOKEN_KEY, data.token);
          const role = mapRole(data?.user?.typecode);
          setUser({
            role,
            name: data?.user?.fullname || "Investor",
            email: data?.user?.email || "",
            phone: data?.user?.phone || "",
            typecode: data?.user?.typecode || "",
          });
          setStatus("ready");
          return { ok: true };
        } catch (err) {
          setStatus("error");
          setError(err.message || "Registration failed");
          return { ok: false };
        }
      },
      continueFree: () => {
        localStorage.removeItem(TOKEN_KEY);
        setUser({ role: "free", name: "Free Explorer" });
      },
      logout: () => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
      },
    };
  }, [user, status, error]);

  return <AuthContext.Provider value={api}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
